import { ClipboardModule } from '@angular/cdk/clipboard';
import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnInit, SimpleChanges, Inject, Optional, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Store } from '@ngrx/store';
import { NgxPaginationModule } from 'ngx-pagination';
import { catchError, Observable, of, switchMap, take, tap } from 'rxjs';
import { IInventionData, IInventionDataResponse } from 'src/app/core/models/results.model';
import { GeneralInnovationActivityResultsService } from 'src/app/features/results/services/general-innovation-activity-results.service';
import { NullableNumberPipe } from 'src/app/shared/pipes/nullable-number.pipe';
import { loadSidenavData } from 'src/app/state/action';
import { AppState, Tab } from 'src/app/state/app.state';
import { selectActiveTabDetails, selectSidenavDataByTabIdAndKey } from 'src/app/state/selectors';
import * as XLSX from 'xlsx';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

@Component({
  selector: 'app-inventions-view',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, NgxPaginationModule, NullableNumberPipe, MatTooltipModule, ClipboardModule],
  templateUrl: './inventions-view.component.html',
  styleUrl: './inventions-view.component.css'
})
export class InventionsViewComponent implements OnInit, OnChanges {
  // Popup support inputs
  @Input() isPopup: boolean = false;
  @Input() popupCompanyName?: string;
  @Input() popupTheme?: string;

  constructor(
    private store: Store<AppState>,
    private apiService: GeneralInnovationActivityResultsService,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    @Optional() @Inject(MAT_DIALOG_DATA) public dialogData?: any,
    @Optional() private dialogRef?: MatDialogRef<InventionsViewComponent>
  ) {
    // Handle popup data if provided
    if (this.dialogData) {
      this.isPopup = this.dialogData.isPopup || false;
      this.popupCompanyName = this.dialogData.popupCompanyName;
      this.popupTheme = this.dialogData.popupTheme;
      this.tabId = this.dialogData.tabId;
      this.query = this.dialogData.query;
      this.entityids = this.dialogData.entityids;
      this.tab_name = this.dialogData.popupCompanyName;
    }
  }

  @Input() tabId!: string;
  @Input() query: string | null = null;
  @Input() entityids: string[] | null = null;
  @Input() tab_name: string | null = null;
  tabs$: Observable<Tab | undefined> = this.store.select(selectActiveTabDetails);

  searchType: string = '';
  showQueryBox: boolean = false;

  ngOnInit(): void {
    // Note: Data fetching is now handled in ngOnChanges for better input handling
  }

  // Method to manually trigger data fetching (can be called from parent)
  public initializePopupData(): void {
    if (this.isPopup) {
      this.searchType = 'company';
      this.getInventionsForPopup();
    }
  }

  // this.tabs$ = this.store.select(selectActiveTabDetails);

  paginator_1_page: number = 1;

  ngOnChanges(changes: SimpleChanges): void {
    // Check if any popup-related inputs have changed
    if (changes['isPopup'] || changes['popupCompanyName'] || changes['popupTheme'] || changes['tabId'] || changes['query'] || changes['entityids']) {
      this.tabs$ = this.store.select(selectActiveTabDetails);
      this.inventions_data = [];
      this.showSpinner = true;
      this.inventions_data_loading_failed = false;
      this.showResults = false;
      this.noInventionsFound = false;
      this.summaryText = '';

      // Check if we're now in popup mode and have all required inputs
      if (this.isPopup && this.query && this.entityids && this.tabId) {
        this.searchType = 'company';
        this.getInventionsForPopup();
      } else if (!this.isPopup) {
        this.store.select(selectActiveTabDetails).pipe(take(1)).subscribe(tab => {
          this.searchType = tab?.search_type ?? '';
        });
        this.getInventionsDataFromStore();
      }
    }
  }

  // Toggle query box
  toggleQueryBox(): void {
    this.showQueryBox = !this.showQueryBox;
  }

  // Close popup method
  closePopup(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }

  getToday(): string {
    return new Date().toISOString().split('T')[0]
  }
  showResults: boolean = false;
  showSpinner: boolean = false;
  noInventionsFound: boolean = false;
  // uses form builder instead of form group and form control to avoid boilerplate code
  form = this.fb.group({
    date_start: [null],
    date_end: [null],
    quality_filter: ['all' as 'all' | 'high']
  });
  inventions_data: IInventionData[] = [];



  //common function to access the api for inventions
  getInventions(): Observable<IInventionDataResponse> {
    this.inventions_data = [];
    return this.apiService.getInventions(
      this.query,
      this.entityids,
      this.form.value.date_start ? new Date(this.form.value.date_start as string) : null,
      this.form.value.date_end ? new Date(this.form.value.date_end as string) : null,
      this.form.value.quality_filter,
      this.searchType,
      this.tabId
    );
  }

  summaryText: string = '';
  loadingSummary: boolean = false;
  //function to retrieve the AI generated Summary from backend
  generateSummary() {

    this.summaryText = "";
    this.loadingSummary = true;
    this.apiService.getAISummary(
      this.query,
      this.entityids,
      this.form.value.date_start ? new Date(this.form.value.date_start as string) : null,
      this.form.value.date_end ? new Date(this.form.value.date_end as string) : null,
      this.form.value.quality_filter,
      this.searchType,
      this.tabId

    ).subscribe({
      next: (res) => {
        this.summaryText = res.payload.summary;
        this.loadingSummary = false;
      },
      error: (err) => {
        alert("AI Summary Generation Failed!!");
        this.loadingSummary = false;
        this.summaryText = "";
      }
    })
  }

  // function to get the inventions when form is submitted
  onSubmit() {
    this.inventions_data = [];
    this.showSpinner = true;
    this.inventions_data_loading_failed = false;
    this.showResults = false;
    this.summaryText = '';
    this.getInventions().pipe(
      tap((res) => {
        if (res.payload.tabId != this.tabId) {
          return;
        }
        this.inventions_data = res.payload.results;
        if (this.inventions_data.length > 0) {
          this.showResults = true;
          this.inventions_data_loading_failed = false;
          this.showSpinner = false;
        } else {
          this.showSpinner = false;
          this.noInventionsFound = true;
        }

      }),
      catchError((err) => {
        console.error(err);
        this.showResults = false;
        this.inventions_data_loading_failed = true;
        this.showSpinner = false;

        return of();
      })
    ).subscribe();
  }


  inventions_data_loading_failed: boolean = false;
  // function to retrive the data from store if its already available. Also stores the data in store
  getInventionsDataFromStore() {
    this.showSpinner = true;
    this.summaryText = '';
    this.store.select(selectSidenavDataByTabIdAndKey(this.tabId, 'inventions_data')).pipe(
      take(1),
      switchMap((data) => {
        if (!data) {
          return this.getInventions().pipe(
            tap((res) => {
              if (res.payload.tabId != this.tabId) {
                this.store.dispatch(loadSidenavData({
                  tabId: res.payload.tabId,
                  sidenavKey: 'inventions_data',
                  data: res.payload.results
                }));
                return;
              }
              this.inventions_data = res.payload.results;
              this.store.dispatch(loadSidenavData({
                tabId: this.tabId,
                sidenavKey: 'inventions_data',
                data: res.payload.results
              }));

              if (this.inventions_data.length > 0) {
                this.showResults = true;
                this.inventions_data_loading_failed = false;
                this.showSpinner = false;
              } else {
                this.showSpinner = false;
                this.noInventionsFound = true;
              }
              this.inventions_data_loading_failed = false;
            }),
            catchError((err) => {
              console.error(err);
              this.showSpinner = false;

              this.inventions_data_loading_failed = true;
              return of(); // return empty observable to complete
            })
          );
        } else {
          this.inventions_data = data;
          if(this.inventions_data.length == 0){
            this.noInventionsFound = true;
          }
          this.inventions_data_loading_failed = false;
          this.showResults = true;
          this.showSpinner = false;

          return of();
        }
      })
    ).subscribe();
  }

  // Popup-specific data fetching method
  getInventionsForPopup() {
    this.paginator_1_page = 1;
    this.showSpinner = true;
    this.summaryText = '';

    this.getInventions().subscribe({
      next: (res) => {
        this.inventions_data = res.payload.results;

        if (this.inventions_data.length > 0) {
          this.showResults = true;
          this.inventions_data_loading_failed = false;
          this.showSpinner = false;
          this.noInventionsFound = false;
          this.cdr.detectChanges(); // Force change detection
        } else {
          this.noInventionsFound = true;
          this.showSpinner = false;
          this.cdr.detectChanges();
        }
      },
      error: (err) => {
        this.inventions_data_loading_failed = true;
        this.showSpinner = false;
        this.cdr.detectChanges();
      }
    });
  }


  // Method to download the Excel file
  downloadExcel(): void {
    // console.log("downloading excel for inventions",   this.inventions_data);
    const filteredList = this.inventions_data.map(item => ({
      'Family ID': item.family_id,
      'Invention Title': item.invention_title,
      'Date Of Publication': item.date_publication,
      'Entity IDs Filing': item.entityids_filing ? item.entityids_filing.join(', ') : '',
      'UCID': item.ucid,
      'Primary Tech Field': item.techfield,
      'Quality Score': Math.round(item.quality_score * 10) / 10,
      'Entity IDs': item.entityids ? item.entityids.join(', ') : '',
      'Applicants Filing': item.applicants_filing ? item.applicants_filing.join(', ') : '',
      'Applicants': item.applicants ? item.applicants.join(', ') : '',
      'Espacenet Link': item.link_espacenet,
    }));


    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(filteredList);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    ws['!cols'] = [
      { wch: 10 }, { wch: 20 }, { wch: 10 }, { wch: 10 }, { wch: 15 },
      { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 20 },
      { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 20 }
    ];
    const sheetName = "Inventions_Data";
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
// if the tab name is too big we shorten it to 30 characters
    const tabName = this.tab_name ? this.tab_name.substring(0, 30) : 'inventions';
    const fileName = this.tab_name ? `${tabName}_inventions.xlsx` : 'excel_file.xlsx';
    XLSX.writeFile(wb, fileName);
  }

}  